import { suggest, formatRupees } from "@theme/vg-bundle-rules";

/**
 * <vg-cart-optimiser> shows at most one bundle suggestion in the cart drawer (spec §5.3).
 * The drawer re-renders on every cart change. Morphing may reuse this element instead of
 * reconnecting it, so data-signature changes also trigger a re-render once the morph has finished.
 *
 * Everything the script builds lives inside [data-vg-optimiser-output], which carries
 * data-skip-subtree-update so morph.js leaves it alone. A re-render that keeps the same signature
 * (e.g. applying a discount) therefore can't wipe a visible suggestion or swap error.
 */
class VgCartOptimiser extends HTMLElement {
  static observedAttributes = ["data-signature"];

  #panel = document.createElement("div");
  #body = document.createElement("div");
  #alert = document.createElement("p");

  constructor() {
    super();
    this.#panel.className = "vg-cart-optimiser__panel";
    this.#panel.hidden = true;
    this.#body.className = "vg-cart-optimiser__body";
    this.#alert.setAttribute("role", "alert");
    this.#alert.dataset.vgSwapStatus = "";
    this.#panel.append(this.#body, this.#alert);
  }

  connectedCallback() {
    this.#render();
  }

  attributeChangedCallback() {
    requestAnimationFrame(() => this.#render());
  }

  /**
   * Called by <vg-swap-button> inside this element. The message stays across re-renders until the
   * next swap starts, which clears it with an empty string. It isn't tied to data-signature because a
   * failed swap's own add and rollback change the signature, and those re-renders can land after the
   * swap has already reported (component-cart-items.js morphs in the cart event's promise callback).
   *
   * @param {string} message
   */
  setSwapError(message) {
    this.#alert.textContent = message;
    this.#syncVisibility();
  }

  #syncVisibility() {
    this.#panel.hidden =
      this.#body.childElementCount === 0 && !this.#alert.textContent;
  }

  #render() {
    const data = this.querySelector('script[type="application/json"]');
    const output = this.querySelector("[data-vg-optimiser-output]");
    if (!data || !output) return;
    if (this.#panel.parentElement !== output)
      output.replaceChildren(this.#panel);

    /** @type {{ lines?: import('./vg-bundle-rules.js').CartLine[], catalog?: import('./vg-bundle-rules.js').CatalogEntry[] }} */
    let parsed;
    try {
      parsed = JSON.parse(data.textContent || "{}");
    } catch (error) {
      console.error("[vg-cart-optimiser]", error);
      this.#body.replaceChildren();
      this.#syncVisibility();
      return;
    }
    const suggestion = suggest(parsed.lines ?? [], parsed.catalog ?? []);

    if (!suggestion) {
      this.#body.replaceChildren();
      this.#syncVisibility();
      return;
    }

    const saves = suggestion.delta < 0;
    const amount = formatRupees(Math.abs(suggestion.delta));

    const message = document.createElement("p");
    message.className = "vg-cart-optimiser__message";
    message.id = "VgCartOptimiserMessage";
    message.textContent = saves
      ? `Switch to ${suggestion.target.title} and save ${amount}.`
      : `Upgrade to ${suggestion.target.title} for ${amount} more.`;

    const swap = document.createElement("vg-swap-button");
    swap.dataset.addVariant = String(suggestion.target.variantId);
    swap.dataset.addHandle = suggestion.target.handle;
    swap.dataset.removeVariants = suggestion.remove
      .map((line) => line.variantId)
      .join(",");
    swap.dataset.error = "Could not update your cart. Please try again.";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "button";
    button.textContent = saves ? "Switch and save" : "Upgrade";
    button.setAttribute("aria-describedby", message.id);

    swap.append(button);
    this.#body.replaceChildren(message, swap);
    this.#syncVisibility();
  }
}

if (!customElements.get("vg-cart-optimiser"))
  customElements.define("vg-cart-optimiser", VgCartOptimiser);
